import './TalkSendModal.scss';
import React, { useState } from 'react';
import Button from '@mui/material/Button';
import { Checkbox, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { useSelector } from 'react-redux';
import fnc from '../../mymod/commFunction';
import { sendDirectMessage } from '../../page_firebase/directMessage/directMessage';
import CircularProgressWithLabel from '../../comm/CircularProgressWithLabel';

/**
 * TalkSendModal
 * props:
 *   title        - 모달 제목 (예: "쪽전 TALK 전송")
 *   list         - [{ isChecked, stCode, name, hp, hp_parent, content }]
 *   onClose      - 모달 닫기 콜백
 */
const TalkSendModal = ({ title, list: initList, onClose }) => {
  const userInfo  = useSelector(state => state.reduxStore.userInfo);

  const [list, setList] = useState(() =>
    initList.map(item => ({ ...item, isChecked: true }))
  );
  const [target, setTarget]     = useState('all');
  const [sending, setSending]   = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults]   = useState([]);

  const normalizePhone = value => String(value || '').replace(/[^0-9]/g, '');

  const sendAxiosPromise = (url, payload) => new Promise(resolve => {
    fnc.sendAxios(url, payload, res => resolve(res));
  });

  // -------------------------------------------------------
  function handleCheckboxChange(e, idx) {
    const newList = [...list];
    newList[idx].isChecked = e.target.checked;
    setList(newList);
    setSelectAll(newList.every(i => i.isChecked));
  }

  const [selectAll, setSelectAll] = useState(true);
  function handleSelectAll() {
    const next = !selectAll;
    setList(list.map(i => ({ ...i, isChecked: next })));
    setSelectAll(next);
  }

  function getSendTargets(checked, acaCode) {
    const targets = [];
    const includeStudent = target === 'all' || target === 'student';
    const includeParent = target === 'all' || target === 'parent';

    checked.forEach(({ item, rowIndex }) => {
      if (includeStudent) {
        targets.push({
          item,
          rowIndex,
          kind: 'student',
          label: '학생',
          receiverId: item.id ? `${acaCode}_${item.id}` : '',
          displayName: item.name
        });
      }

      if (includeParent) {
        const parentPhone = normalizePhone(item.parentPhone || item.hpParent || item.hp_parent);
        targets.push({
          item,
          rowIndex,
          kind: 'parent',
          label: '부모',
          receiverId: parentPhone ? `${acaCode}_P_${parentPhone}` : '',
          displayName: item.parentName || `${item.name} 학부모`,
          parentPhone
        });
      }
    });

    return targets;
  }

  async function ensureGuardianTalkTargets(sendTargets, acaCode) {
    const guardians = sendTargets
      .filter(sendTarget => sendTarget.kind === 'parent' && sendTarget.receiverId && sendTarget.item.id)
      .map(sendTarget => ({
        studentId: sendTarget.item.id,
        studentStCode: sendTarget.item.stCode || 0,
        studentName: sendTarget.item.name || '',
        guardianId: sendTarget.receiverId,
        guardianName: sendTarget.displayName,
        guardianPhone: sendTarget.parentPhone,
        relation: 'parent',
        status: 'pending',
        isPrimary: 'Y'
      }))
      .filter((guardian, index, array) =>
        index === array.findIndex(item =>
          item.studentId === guardian.studentId && item.guardianId === guardian.guardianId
        )
      );

    if (!guardians.length) return;

    const syncResult = await sendAxiosPromise('/socialLoginRoutes/upsert/guardianTalkTargets', {
      acaCode,
      guardians
    });
    if (syncResult?.failed?.length) {
      console.warn('[TalkSend] 일부 학부모 매칭 저장 실패:', syncResult.failed);
    }
  }

  function getTalkOptions(sendTarget, chatRoomName) {
    if (sendTarget.kind !== 'parent') return {};
    const studentId = sendTarget.item.id || '';
    const studentName = sendTarget.item.name || '';

    return {
      targetRole: 'parent',
      contextType: 'student',
      contextId: studentId,
      contextKey: `student_${studentId}`,
      studentId,
      studentName,
      roomName: `${chatRoomName} - ${studentName} 학부모`
    };
  }

  // -------------------------------------------------------
  async function onSendTalk() {
    const checked = list
      .map((item, rowIndex) => ({ item, rowIndex }))
      .filter(({ item }) => item.isChecked);
    if (!checked.length) { alert('전송할 학생을 선택하세요.'); return; }

    const certCode  = fnc.getCookie('certCode') || '';
    const acaCode   = certCode;
    const SENDER_ID   = `${acaCode}_${userInfo?.id || ''}`;
    const SENDER_NAME = userInfo?.name || '';
    const chatRoomName = 'AI K-EDU 학원';
    const sendTargets = getSendTargets(checked, acaCode);

    if (!sendTargets.length) { alert('전송할 TALK 대상을 선택하세요.'); return; }
    if (!window.confirm(`선택한 TALK 대상 ${sendTargets.length}명에게 전송하시겠습니까?`)) return;

    setSending(true);
    setProgress(0.01);
    setResults([]);

    const sendResults = [];

    try {
      await ensureGuardianTalkTargets(sendTargets, acaCode);
    } catch (err) {
      console.warn('[TalkSend] 학부모 매칭 저장 중 오류:', err);
    }

    for (let i = 0; i < sendTargets.length; i++) {
      const sendTarget = sendTargets[i];
      const item = sendTarget.item;
      const receiverId = sendTarget.receiverId;
      const text = item.content || '';

      console.log(`[TalkSend] ${i+1}/${sendTargets.length}`, {
        name: sendTarget.displayName,
        target: sendTarget.label,
        itemId: item.id,
        SENDER_ID,
        receiverId,
        text,
      });

      if (!receiverId) {
        const msg = sendTarget.kind === 'parent'
          ? '부모 휴대폰 없음'
          : '학생 앱 ID 없음 (미가입)';
        sendResults.push({ rowIndex: sendTarget.rowIndex, targetLabel: sendTarget.label, success: false, msg });
        setProgress(((i + 1) / sendTargets.length) * 100);
        continue;
      }
      if (!text) {
        sendResults.push({ rowIndex: sendTarget.rowIndex, targetLabel: sendTarget.label, success: false, msg: '전송 내용 없음' });
        setProgress(((i + 1) / sendTargets.length) * 100);
        continue;
      }

      try {
        const res = await sendDirectMessage(
          chatRoomName,
          SENDER_ID,
          SENDER_NAME,
          receiverId,
          text,
          'text',
          null,
          getTalkOptions(sendTarget, chatRoomName)
        );
        console.log(`[TalkSend] 결과:`, res);
        sendResults.push({
          rowIndex: sendTarget.rowIndex,
          targetLabel: sendTarget.label,
          success: res.success,
          msg: res.success ? '전송 성공' : `전송 실패: ${res.error}`
        });
      } catch (err) {
        console.error(`[TalkSend] 오류:`, err);
        sendResults.push({
          rowIndex: sendTarget.rowIndex,
          targetLabel: sendTarget.label,
          success: false,
          msg: `오류: ${err.message}`
        });
      }
      setProgress(((i + 1) / sendTargets.length) * 100);
      await new Promise(r => setTimeout(r, 300));
    }

    setResults(sendResults);
    setSending(false);
    setProgress(100);
    setTimeout(() => setProgress(0), 1500);

    const ok = sendResults.filter(r => r.success).length;
    alert(`TALK 전송 완료 (성공: ${ok}/${sendResults.length})`);
  }

  // -------------------------------------------------------
  return (
    <div className="TalkSendModal">
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal-box">
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn-close" onClick={onClose}>&#x2715;</button>
        </div>

        {!!progress && (
          <div className="box-progress">
            <CircularProgressWithLabel value={progress} />
          </div>
        )}

        <div className="modal-body">
          <div className="option-target">
            <span className="option-label">수신 대상</span>
            <RadioGroup row value={target} onChange={e => setTarget(e.target.value)}>
              <FormControlLabel value="all" control={<Radio />} label="모두" disabled={sending} />
              <FormControlLabel value="student" control={<Radio />} label="학생" disabled={sending} />
              <FormControlLabel value="parent" control={<Radio />} label="부모" disabled={sending} />
            </RadioGroup>
          </div>

          <table className="table-hong table-talk">
            <thead>
              <tr>
                <th>
                  <Checkbox
                    className="custom-checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>성명</th>
                <th>HP학생</th>
                <th>HP부모</th>
                <th>전송내용</th>
                <th>결과</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item, idx) => {
                const rowResults = results.filter(result => result.rowIndex === idx);
                const hasFailure = rowResults.some(result => !result.success);
                const isComplete = rowResults.length > 0 && !hasFailure;
                return (
                  <tr key={idx} className={rowResults.length ? (isComplete ? 'row-success' : 'row-fail') : ''}>
                    <td>
                      <Checkbox
                        className="custom-checkbox"
                        checked={item.isChecked}
                        onChange={e => handleCheckboxChange(e, idx)}
                      />
                    </td>
                    <td>{item.name}</td>
                    <td>{item.hp}</td>
                    <td>{item.hp_parent}</td>
                    <td className="td-content">{item.content}</td>
                    <td className="td-result">
                      {rowResults.map((result, resultIndex) => (
                        <div key={`${result.targetLabel}-${resultIndex}`}>
                          {result.targetLabel}: {result.msg}
                        </div>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="modal-footer">
          <Button className="h-btn-blue btn-send" onClick={onSendTalk} disabled={sending}>
            <i className="fa-solid fa-comment-dots" />
            {sending ? 'TALK 전송 중...' : 'TALK 전송'}
          </Button>
          <Button className="h-btn-gray btn-cancel" onClick={onClose}>
            닫기
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TalkSendModal;
